import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryClient';
import { Room } from '@/types';
import {
  fetchRooms,
  fetchRoom,
  fetchRoomTypes,
  createRoom,
  updateRoom,
  deleteRoom,
  fetchRoomDevices,
  updateDeviceRoom,
  type CreateRoomRequest,
  type UpdateRoomRequest,
} from '@/api/rooms';

export type { Room };

/**
 * Hook to fetch all rooms, optionally filtered by site
 */
export function useRooms(siteId?: string) {
  return useQuery({
    queryKey: queryKeys.rooms.list(siteId),
    queryFn: async () => {
      const response = await fetchRooms(siteId);
      return response.rooms;
    },
  });
}

/**
 * Hook to fetch a single room
 */
export function useRoom(id: string) {
  return useQuery({
    queryKey: queryKeys.rooms.detail(id),
    queryFn: async () => {
      const response = await fetchRoom(id);
      return response.room;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch available room types
 */
export function useRoomTypes() {
  return useQuery({
    queryKey: queryKeys.rooms.types(),
    queryFn: async () => {
      const response = await fetchRoomTypes();
      return response.types;
    },
    staleTime: 5 * 60 * 1000, // Room types don't change often
  });
}

/**
 * Hook to create a new room
 */
export function useCreateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRoomRequest) => {
      return createRoom(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

/**
 * Hook to update a room
 */
export function useUpdateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRoomRequest }) => {
      return updateRoom(id, data);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.detail(id) });
    },
  });
}

/**
 * Hook to delete a room
 */
export function useDeleteRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return deleteRoom(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

/**
 * Hook to fetch devices in a room
 */
export function useRoomDevices(roomId: string) {
  return useQuery({
    queryKey: queryKeys.rooms.devices(roomId),
    queryFn: async () => {
      return fetchRoomDevices(roomId);
    },
    enabled: !!roomId,
  });
}

/**
 * Hook to update a device's room assignment
 */
export function useUpdateDeviceRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, roomId }: { deviceId: string; roomId: string | null }) => {
      return updateDeviceRoom(deviceId, roomId);
    },
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
    },
  });
}
